import React, { useEffect, useMemo } from 'react';
import { TextVariant } from '@naf/text';
import { useDispatch } from 'react-redux';
import { ProductPriceItem } from '@components/MembershipBox/Types';
import { CompareMembershipBlockType } from '@appTypes/CompareMembershipBlockType';
import { ExpandableMembershipBox } from '@components/MembershipBox/ExpandableMembershipBox';
import { actions as getProductsAction } from '@redux/modules/products';
import useSelector from '@redux/typedHooks';
import { mergeProductsWithAddOns } from '@components/MembershipBox/productUtils';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { LinkField } from './LinkField';
import * as S from './CompareMembershipBlock.styles';

const CompareMembershipBlockComponent = ({ block }: { block: CompareMembershipBlockType }) => {
  const dispatch = useDispatch();
  const productsState = useSelector((state) => state.products);
  const isMobile = useMediaQuery('(max-width: 576px)');

  useEffect(() => {
    if (!productsState.hasFetched && !productsState.isUpdating) {
      dispatch(getProductsAction.getProducts.request(undefined));
    }
  }, [dispatch, productsState.hasFetched, productsState.isUpdating]);

  const mergedProducts = useMemo(() => {
    if (!productsState?.data?.products) return [];

    const eligibleProducts = Object.values(productsState.data.products);

    return mergeProductsWithAddOns({
      eligibleProducts,
    });
  }, [productsState?.data?.products]);

  const productsById = useMemo(() => {
    const map = new Map();

    mergedProducts.forEach((product) => {
      map.set(product.productNumber, product);
    });

    return map;
  }, [mergedProducts]);

  if (!block) return null;

  return (
    <S.CompareBlockWrapper>
      {block?.blockTitle && (
        <S.CompareBlockTitle>
          <S.CompareTitle variant={TextVariant.Header1}>{block?.blockTitle}</S.CompareTitle>
          {block?.cta && (
            <S.DesktopLinkWrapper>
              <LinkField link={block?.cta} />
            </S.DesktopLinkWrapper>
          )}
        </S.CompareBlockTitle>
      )}
      <S.CompareBlocks>
        {block?.memberships?.map((membership) => {
          const product = productsById.get(membership.productId);
          const productPrice = product?.productAppPrice?.['1']?.itemTotalPrice?.toString() ?? '';
          const householdPrices = product?.productAppPrice
            ? Object.values(product.productAppPrice as Record<string, ProductPriceItem>).map((priceItem) => ({
                title: `${priceItem.itemCount} ${priceItem.itemCount === 1 ? 'person' : 'personer'}`,
                price: priceItem.itemTotalPrice.toString(),
              }))
            : [];

          return (
            <ExpandableMembershipBox
              key={membership?.title}
              tag={membership?.tag}
              title={membership?.title}
              variant={membership?.variant}
              openByDefaultOnDesktop={membership?.openByDefaultOnDesktop}
              showPriceInline={membership?.showPriceInline}
              terms={membership?.terms}
              householdMembers={membership?.householdMembers}
              benefitAccordions={membership?.benefitAccordions}
              cta={membership?.cta || null}
              product={product}
              productPrice={productPrice}
              householdPrices={householdPrices}
              isMobile={isMobile}
            />
          );
        })}
      </S.CompareBlocks>
      {block?.cta && (
        <S.MobileLinkWrapper>
          <LinkField link={block?.cta} />
        </S.MobileLinkWrapper>
      )}
    </S.CompareBlockWrapper>
  );
};

export const CompareMembershipBlock = React.memo(CompareMembershipBlockComponent);
